import { __values } from "tslib";
import { logValidateConfigEquality } from '../telemetry/logValidateConfigEquality';
import { logValidateConfigEqualityError } from '../telemetry/logValidateConfigEqualityError';
/**
 * Determines if the clientConfig is equal to the serverConfig.
 * Runs recursive helper function to compare all shared values between the two configs.
 * If shared values are not equal, logs error and returns false.
 * @returns
 */
export function validateConfigEquality(serverConfig, clientConfig, queryKey, maxDepth) {
    if (maxDepth === void 0) { maxDepth = 3; }
    var _a = validateConfigEqualityHelper(serverConfig, clientConfig, maxDepth), detectedMismatches = _a.detectedMismatches, error = _a.error;
    if (error) {
        var errorMsg = void 0;
        if (error instanceof Error) {
            errorMsg = error.message;
        }
        else {
            errorMsg = error;
        }
        logValidateConfigEqualityError("Error occurred while validating config equality:\n\n".concat(errorMsg));
        return false;
    }
    // if mismatches detected return false and log error
    if (detectedMismatches.length > 0) {
        logValidateConfigEquality({
            QueryKey: queryKey,
            Mismatches: detectedMismatches.join(','),
        });
        return false;
    }
    return true;
}
/**
 * Checks that all values shared between clientConfig and serverConfig are equal.
 * Recursively checks objects and arrays up to maxDepth.
 * Values present in only one config are ignored.
 * Provides a list of all detected mismatches.
 */
export function validateConfigEqualityHelper(serverConfig, clientConfig, maxDepth) {
    var e_1, _a, e_2, _b;
    var detectedMismatches = [];
    /**
     * Helper function that adds a mismatch to the detectedMismatches array.
     * Includes info on how mismatch occurs.
     * Logs bool / num values as is, logs string values as their length.
     * This is not ideal, but is a compromise to avoid logging sensitive data.
     * Check validateConfigEquality.test.ts for formatting examples.
     */
    var recordMismatchForLevel = function (_a) {
        var _b, _c;
        var key = _a.key, mismatchType = _a.mismatchType, val1 = _a.val1, val2 = _a.val2;
        // String values should not be logged, log their length instead
        // SubLvl strings can be logged as is
        var val1Str = typeof val1 === 'string' && mismatchType !== 'SubLvl'
            ? "StrLen:".concat(val1.length.toString())
            : (_b = val1 === null || val1 === void 0 ? void 0 : val1.toString()) !== null && _b !== void 0 ? _b : 'undefined';
        var val2Str = typeof val2 === 'string' && mismatchType !== 'SubLvl'
            ? "StrLen:".concat(val2.length.toString())
            : (_c = val2 === null || val2 === void 0 ? void 0 : val2.toString()) !== null && _c !== void 0 ? _c : 'undefined';
        var mismatchInfo = "".concat(key, ":[").concat(mismatchType, "(").concat(val1Str);
        if (val2 !== undefined) {
            mismatchInfo += "|".concat(val2Str);
        }
        mismatchInfo += ')]';
        detectedMismatches.push(mismatchInfo);
    };
    try {
        // base
        if (maxDepth <= 0) {
            return { detectedMismatches: detectedMismatches };
        }
        try {
            // recursive
            for (var _c = __values(Object.keys(clientConfig)), _d = _c.next(); !_d.done; _d = _c.next()) {
                var key = _d.value;
                var clientVal = clientConfig[key];
                var serverVal = serverConfig[key];
                if (serverVal !== undefined) {
                    if (typeof clientVal === 'object' && clientVal !== null) {
                        // Set equality
                        if (clientVal instanceof Set) {
                            // Sets should be the same size
                            if (clientVal.size !== serverVal.size) {
                                recordMismatchForLevel({
                                    key: key,
                                    mismatchType: 'SetLen',
                                    val1: serverVal.size,
                                    val2: clientVal.size,
                                });
                                continue;
                            }
                            try {
                                // All items in clientVal should be in serverVal
                                for (var clientVal_1 = (e_2 = void 0, __values(clientVal)), clientVal_1_1 = clientVal_1.next(); !clientVal_1_1.done; clientVal_1_1 = clientVal_1.next()) {
                                    var item = clientVal_1_1.value;
                                    if (!serverVal.has(item)) {
                                        recordMismatchForLevel({
                                            key: key,
                                            mismatchType: 'SetMis',
                                            val1: item,
                                        });
                                        continue;
                                    }
                                }
                            }
                            catch (e_2_1) { e_2 = { error: e_2_1 }; }
                            finally {
                                try {
                                    if (clientVal_1_1 && !clientVal_1_1.done && (_b = clientVal_1.return)) _b.call(clientVal_1);
                                }
                                finally { if (e_2) throw e_2.error; }
                            }
                        }
                        else {
                            // Object or Array equality
                            if (clientVal instanceof Array) {
                                if (clientVal.length !== serverVal.length) {
                                    recordMismatchForLevel({
                                        key: key,
                                        mismatchType: 'ArrLen',
                                        val1: serverVal.length,
                                        val2: clientVal.length,
                                    });
                                    continue;
                                }
                            }
                            // Recursively check sub-levels of Objects and Arrays
                            var subLevelMismatches = validateConfigEqualityHelper(serverVal, clientVal, maxDepth - 1).detectedMismatches;
                            if (subLevelMismatches.length > 0) {
                                recordMismatchForLevel({
                                    key: key,
                                    mismatchType: 'SubLvl',
                                    val1: subLevelMismatches.join(','),
                                });
                            }
                        }
                    }
                    else if (clientVal !== serverVal) {
                        recordMismatchForLevel({
                            key: key,
                            mismatchType: 'KeyMis',
                            val1: serverVal,
                            val2: clientVal,
                        });
                    }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return { detectedMismatches: detectedMismatches };
    }
    catch (error) {
        return { detectedMismatches: detectedMismatches, error: error };
    }
}
//# sourceMappingURL=validateConfigEquality.js.map